import {
  Box,
  Grid,
  HStack,
  IconButton,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Text,
  useMediaQuery,
  VStack,
} from "@chakra-ui/react";
import { Image } from "../reusables";
import { AnimatePresence, motion } from "framer-motion";
import { FiX } from "react-icons/fi";
import { FaPlay } from "react-icons/fa";
import { FaPause } from "react-icons/fa6";
import dayjs from "../../helpers/dayJs";
import { TbReload } from "react-icons/tb";
import useColorAccent from "../../hooks/useColorAccent";
import { Link, useNavigate } from "react-router";
import { useHlsAudioActions, useHlsAudioState } from "../../wrappers/hlsAudio";

const MotionBox = motion(Box);
const MotionGrid = motion(Grid);

/**
 *
 * @param {import("@chakra-ui/react").GridProps} props
 */
const MusicPlayer = (props) => {
  const { currentTrackData, hlsTrackState, isInRoom } = useHlsAudioState();
  const { seekTrack, pauseTrack, resumePlay, reset } = useHlsAudioActions();
  const { attachImage, accentColor } = useColorAccent();
  const navigate = useNavigate();
  const [isMobile] = useMediaQuery("(max-width: 600px)");

  function handlePlayPause() {
    if (hlsTrackState.isPlaying) {
      pauseTrack();
    } else if (hlsTrackState.currentTime == hlsTrackState.duration) {
      seekTrack(0);
      resumePlay();
    } else {
      resumePlay();
    }
  }

  const thumbnailFileName =
    currentTrackData?.track?.thumbnails?.[
      currentTrackData?.track?.thumbnails?.length - 1
    ]?.fileName;

  function navigateToPost() {
    navigate(
      `user/${currentTrackData?.user?.username}/post/${currentTrackData?.id}`
    );
  }

  const isTrackPlayCompleted =
    hlsTrackState.currentTime > 0 &&
    hlsTrackState.currentTime == hlsTrackState.duration;

  return (
    <AnimatePresence>
      {currentTrackData && !isInRoom && (
        <MotionGrid
          userSelect={"none"}
          key="music_player"
          gridTemplateRows={"225px auto"}
          w={{ lg: "400px", base: "full" }}
          h={"max-content"}
          borderRadius={"xl"}
          pos="absolute"
          bottom={{ lg: "4", base: "0" }}
          right={{ lg: "4", base: "0" }}
          boxShadow={`0px 0px 15px 5px ${accentColor}20`}
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{
            scale: 0.95,
            opacity: 0,
            transition: { duration: 0.1 },
          }}
          transition={{ type: "keyframes", duration: 0.17 }}
          role="group"
          onDoubleClick={() => !isInRoom && navigateToPost()}
          bg="#313134"
        >
          <Box pos="relative">
            <Image
              borderRadius={"xl"}
              firebaseStoragePath={
                thumbnailFileName
                  ? `photos/${currentTrackData.user?.id}/${currentTrackData.id}/thumbnails/${thumbnailFileName}`
                  : null
              }
              onLoad={attachImage}
              h="225px"
            />
            <Grid
              gridTemplateRows={"repeat(3, 1fr)"}
              opacity={
                hlsTrackState.currentTime == hlsTrackState.duration ||
                hlsTrackState.isLoading ||
                isMobile
                  ? 1
                  : 0
              }
              _groupHover={{
                opacity: 1,
              }}
              pos="absolute"
              top="0"
              left={"0"}
              right={"0"}
              bottom={"0"}
              bg="#00000070"
              borderRadius={"xl"}
            >
              <HStack justify={"flex-end"} align={"flex-start"} pr="1">
                <IconButton
                  icon={<FiX size={24} />}
                  size="lg"
                  variant={"glow"}
                  h="max-content"
                  pl="2"
                  py="2"
                  onClick={reset}
                />
              </HStack>
              <VStack justify={"center"} align={"center"}>
                <IconButton
                  variant={"glow"}
                  fontSize={"34px"}
                  icon={
                    isTrackPlayCompleted ? (
                      <TbReload />
                    ) : hlsTrackState.isPlaying ? (
                      <FaPause />
                    ) : (
                      <FaPlay />
                    )
                  }
                  size="lg"
                  isLoading={hlsTrackState.isLoading}
                  onClick={handlePlayPause}
                />
              </VStack>
              <VStack
                align={"flex-start"}
                justify={"flex-end"}
                w="full"
                px="2"
                pr="1"
              >
                <Text
                  fontSize={"10px"}
                  fontWeight={"semibold"}
                  px="1"
                  py="0.5"
                  bg="white"
                  color="black"
                  borderRadius={"md"}
                >
                  {dayjs
                    .utc(
                      (hlsTrackState.isPlaying
                        ? hlsTrackState.currentTime
                        : currentTrackData?.duration) * 1000
                    )
                    .format("mm:ss")}
                </Text>
                <Slider
                  max={hlsTrackState.duration}
                  defaultValue={0}
                  value={hlsTrackState.currentTime}
                  w="full"
                  onChange={seekTrack}
                  mb="-2"
                >
                  <SliderTrack>
                    <SliderFilledTrack />
                  </SliderTrack>
                  <SliderThumb />
                </Slider>
              </VStack>
            </Grid>
          </Box>
          <VStack
            align={"flex-start"}
            color="white"
            spacing={"0"}
            px="4"
            py="4"
            pt="2.5"
            // pb='2.5'
          >
            <Text
              fontSize={"md"}
              fontWeight={"semibold"}
              as={Link}
              to={`/user/${currentTrackData?.user?.username}/post/${currentTrackData.id}`}
            >
              {currentTrackData?.title}
            </Text>
            <HStack fontSize={"xs"} spacing={"1"}>
              <Text color="gray.400">by</Text>
              <Text as={Link} to={`/user/${currentTrackData?.user?.username}`}>
                {currentTrackData?.user?.fullName}
              </Text>
            </HStack>
          </VStack>
        </MotionGrid>
      )}
    </AnimatePresence>
  );
};

export default MusicPlayer;
